// Copyright ©️ 2026 eVolve MEP, LLC

import { Fragment, useState } from 'react';

import { type WorkOrderId, FLAT_BLACK_FILLER, type MappedSpool } from '@evolvemep/api-ts';
import { Accordion, Box, Divider, Flex, Text } from '@mantine/core';

import { ColorPickerBar } from 'components/Mantine/ColorPickerBar';
import { EntityStatusPoint } from 'components/Mantine/EntityStatusPoint';
import { EvolveLink } from 'components/Mantine/Navigation/EvolveLink';
import { hasLengthAtLeast } from 'helpers/helper-types';
import { isNil } from 'helpers/isNotNil';
import { SpoolAccordionControls } from 'modules/Shop/Spooling/Assigning/SpoolAccordionControls';
import { SpoolCard } from 'modules/Shop/Spooling/Assigning/SpoolCard';
import { UnassignSpoolModal } from 'modules/Shop/Spooling/Assigning/UnassignSpoolModal';
import { type MappedRenderableSpool, useSpoolAssignment } from 'modules/Shop/Spooling/hooks/useSpoolAssignment';
import { getTaskStatusFromItem } from 'modules/Shop/WorkOrders/WorkOrderDetail/common';

import { VisibilityToggle } from './VisibilityToggle';

type Props = {
  filteredAssignedSpools: [WorkOrderId, readonly MappedRenderableSpool[]][];
  disabled: boolean;
  colorKeyEnabled: boolean;
  refresh: () => void;
};

export const AssignedSpools = ({ filteredAssignedSpools, disabled, colorKeyEnabled, refresh }: Props) => {
  const {
    assignedSpoolColorScheme,
    updateAssignedSpoolColor,
    selectedSpools,
    setSpoolVisibility,
    spoolVisibility,
    loadingAssignedSpools,
  } = useSpoolAssignment();

  const toggleWrGroupVisibility = (visible: boolean, groupIndex: number) => {
    const [_, innerSpools] = filteredAssignedSpools[groupIndex] ?? [];
    if (isNil(innerSpools)) return;
    setSpoolVisibility({
      spoolNames: innerSpools.map(({ name }) => name),
      visible,
    });
  };

  const [unmappingSpool, setUnmappingSpool] = useState<MappedSpool>();
  return (
    <>
      <Accordion.Item value="assigned">
        <SpoolAccordionControls
          label="Assigned Spools"
          loading={loadingAssignedSpools}
          itemCount={loadingAssignedSpools ? null : filteredAssignedSpools.flatMap(([_, inner]) => inner).length}
          visibilityProps={{
            visible: filteredAssignedSpools.some(([_, inner]) =>
              inner.some(({ name }) => spoolVisibility[name] ?? false),
            ),
            disabled: disabled || selectedSpools.length > 0,
            toggleAll: (visible: boolean) =>
              setSpoolVisibility({
                spoolNames: filteredAssignedSpools.flatMap(([_, inner]) => inner.map((s) => s.name)),
                visible,
              }),
          }}
        />
        <Accordion.Panel>
          <Accordion
            multiple
            styles={{
              label: { fontWeight: 'bold' },
              content: { padding: 0 },
              item: { borderBottom: 'none' },
            }}
          >
            {filteredAssignedSpools.map(
              ([workOrderId, innerSpools], i) =>
                hasLengthAtLeast(innerSpools, 1) && (
                  <Accordion.Item value={workOrderId} key={workOrderId}>
                    <Divider />
                    <Flex>
                      {colorKeyEnabled && (
                        <ColorPickerBar
                          label="Work Order Spools Color"
                          value={assignedSpoolColorScheme[workOrderId] ?? FLAT_BLACK_FILLER}
                          onChange={(newColor) => updateAssignedSpoolColor(workOrderId, newColor)}
                          transparent={!innerSpools.some(({ name }) => spoolVisibility[name] ?? false)}
                        />
                      )}
                      <Box flex={1} miw={0}>
                        <Accordion.Control h={40} bg="gray.1" px="lg" className="selectable-spool">
                          <Flex align="center" justify="space-between" mr="xs" gap="xs">
                            <Flex align="center" gap="xs" fz="sm" miw={0}>
                              <EntityStatusPoint
                                customStatusEntityName="Work Order"
                                stateName={innerSpools[0].workOrderStateName}
                                statusId={innerSpools[0].workOrderStatusId}
                              />
                              <Text
                                lineClamp={1}
                                title={innerSpools[0].workOrderName}
                                style={{ textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                              >
                                {innerSpools[0].workOrderName}
                              </Text>
                              <EvolveLink
                                to={`/work-orders/${innerSpools[0].workOrderId}`}
                                from="Spooling"
                                style={{ flexShrink: 0 }}
                              >
                                <Text c="dimmed" lineClamp={1}>
                                  {innerSpools[0].workOrderIdentifier}
                                </Text>
                              </EvolveLink>
                              <Text c="dimmed" textWrap="nowrap">
                                ({innerSpools.length})
                              </Text>
                            </Flex>
                            <VisibilityToggle
                              visible={innerSpools.some(({ name }) => spoolVisibility[name] ?? false)}
                              disabled={disabled || selectedSpools.length > 0}
                              onToggle={(visible) => toggleWrGroupVisibility(visible, i)}
                            />
                          </Flex>
                        </Accordion.Control>
                        <Accordion.Panel bg="gray.0">
                          {innerSpools.map((spool) => (
                            <Fragment key={spool.name}>
                              <Divider />
                              <SpoolCard
                                key={spool.name}
                                spool={spool}
                                disableVisibility={disabled || selectedSpools.length > 0}
                                isVisible={spoolVisibility[spool.name] ?? false}
                                toggleVisibility={() =>
                                  setSpoolVisibility({
                                    spoolNames: [spool.name],
                                    visible: !spoolVisibility[spool.name],
                                  })
                                }
                                beforeTitle={
                                  <EntityStatusPoint
                                    customStatusEntityName="Task"
                                    stateName={getTaskStatusFromItem(spool)}
                                  />
                                }
                                menuProps={{
                                  disabled,
                                  options: ['Unassign'],
                                  onMenuItemSelected: () => setUnmappingSpool(spool),
                                }}
                              />
                            </Fragment>
                          ))}
                        </Accordion.Panel>
                      </Box>
                    </Flex>
                  </Accordion.Item>
                ),
            )}
          </Accordion>
          {filteredAssignedSpools.length === 0 && (
            <Text c="dimmed" p="md" fz="sm">
              No results found.
            </Text>
          )}
        </Accordion.Panel>
      </Accordion.Item>
      <UnassignSpoolModal refresh={refresh} onClose={() => setUnmappingSpool(undefined)} spool={unmappingSpool} />
    </>
  );
};
